import {
  EXPERIENCE_CONTENT_TABLE,
  GEOXP_AUDIO_BUCKET,
  GEOXP_SPOTS_TABLE,
  GEOXP_VIDEO_BUCKET,
} from '../constants/db.constants'
import { parseError } from './stream'

import { getAudioFileUrl, supabase } from './supabase.helpers'
import { AgamiSpot } from '../../types/supabase'

export const getAgami = async ({
  agamiId,
}: {
  agamiId: string
}): Promise<{
  agamiData?: AgamiSpot
  error?: string | number | boolean | Record<string, unknown> | unknown[] | null
  err?: unknown
}> => {
  try {
    const { data: agamiData, error } = await supabase.functions.invoke('get-agami-details', {
      body: JSON.stringify({ agamiId }),
    })
    if (!error) {
      return { agamiData }
    }
    const parsedError = await parseError(error)
    return { agamiData, error: parsedError }
  } catch (err) {
    return { err }
  }
}

export const getUserAgamiListWithExpRelations = async ({
  userId,
  getAudioUrls,
}: {
  userId: string
  getAudioUrls?: boolean
}): Promise<{ spots?: AgamiSpot[]; error?: Record<string, unknown> | null | unknown }> => {
  try {
    const { data, error } = await supabase
      .from(GEOXP_SPOTS_TABLE)
      .select('*, experience_content_spots (experience_id, manual_code)')
      .eq('user_id', userId)
      .eq('experience_content_spots.is_canceled', false)
      .eq('is_canceled', false)
      .order('updated_at', { ascending: false })

    if (error) {
      return { error }
    }

    if (!getAudioUrls) {
      return { spots: data }
    }

    const fileUrlPromises = data.map((spot: AgamiSpot) =>
      spot.audio_content_path
        ? getAudioFileUrl({
            bucketId: spot.content_media_type === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
            filePath: spot.audio_content_path,
          })
        : Promise.resolve(null)
    )

    const fileUrls = await Promise.all(fileUrlPromises)

    const spots = data.map((spot: AgamiSpot, idx: number) => ({
      ...spot,
      audioUrl: (fileUrls[idx]?.data as { signedUrl?: string })?.signedUrl,
    }))

    return { spots, error }
  } catch (error) {
    return { error }
  }
}

export const getUserAgamiList = async ({
  userId,
  getAudioUrls,
}: {
  userId: string
  getAudioUrls?: boolean
}): Promise<{
  spots?: AgamiSpot[]
  data?: Record<string, unknown>[]
  error?: Record<string, unknown> | unknown
  err?: unknown
}> => {
  try {
    const { data, error } = await supabase
      .from(GEOXP_SPOTS_TABLE)
      .select('*')
      .eq('user_id', userId)
      .eq('is_canceled', false)
      .order('updated_at', { ascending: false })

    if (error) {
      return { error }
    }

    if (!getAudioUrls) {
      return { spots: data }
    }

    if (data.length === 0) {
      return { data }
    }

    const fileUrlPromises = data.map((spot: AgamiSpot) =>
      spot.audio_content_path
        ? getAudioFileUrl({
            bucketId: spot.content_media_type === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
            filePath: spot.audio_content_path,
          })
        : Promise.resolve(null)
    )

    const fileUrls = await Promise.all(fileUrlPromises)

    const spots = data.map((spot: AgamiSpot, idx: number) => ({
      ...spot,
      audioUrl: (fileUrls[idx]?.data as { signedUrl?: string })?.signedUrl,
    }))

    return { spots, error }
  } catch (err) {
    return { err }
  }
}

export const getUserGeoXpSpotCount = async (): Promise<{
  count?: number
  error?: Record<string, unknown> | unknown | null
}> => {
  const { data, error } = await supabase.rpc('get_user_spots_count')

  if (error) {
    return { error }
  }

  return { count: data, error }
}

export const deleteGeoXpSpot = async ({
  userId,
  spotId,
  mediaPath,
  mediaType,
}: {
  userId: string
  spotId: string
  mediaPath: string | null
  mediaType: number
}): Promise<{
  error?: Record<string, unknown> | unknown
  data?: { expTable?: Record<string, unknown>[]; expContentTable?: Record<string, unknown>[] }
}> => {
  // A text-only Agami has no storage object by design. Missing media also must
  // not make the database record impossible to remove.
  if (mediaPath) {
    const { error: removeError } = await supabase.storage
      .from(mediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET)
      .remove([mediaPath])

    if (removeError) {
      return { error: removeError }
    }
  }

  // step 2 -delete from SPOTS table
  const spotsTableUpdate = await supabase
    .from(GEOXP_SPOTS_TABLE)
    .update({ is_canceled: true })
    .eq('id', spotId)
    .eq('user_id', userId)
    .select()

  if (spotsTableUpdate.error) {
    return {
      error: spotsTableUpdate.error,
    }
  }

  // step 3 -delete all of its relationships with experiences
  const expContentTableUpdate = await supabase
    .from(EXPERIENCE_CONTENT_TABLE)
    // set is_canceled to true and clear manual_code to avoid conflicts
    .update({ is_canceled: true, manual_code: null })
    .eq('spot_id', spotId)
    .select()

  if (expContentTableUpdate.error) {
    return {
      error: expContentTableUpdate.error,
    }
  }

  return {
    data: {
      expTable: spotsTableUpdate.data,
      expContentTable: expContentTableUpdate.data,
    },
  }
}

/**
 * Every published agami in an experience, whoever authored it.
 *
 * Deliberately a SECOND function next to getSpotsByExperienceId, which asks a
 * different question - "MY agami in this experience" - and must keep asking it:
 * it is what keeps the owner's dashboard on Agami Global readable, where the
 * content belongs to 19 different authors. Generalising the existing query with
 * an `includeAllAuthors` flag would be a door someone eventually opens from the
 * wrong side.
 *
 * ⚠️ The `is_draft` filter is what keeps the owner's work-in-progress out of a
 * recipient's view (story 02.3, AC-5). It is the first line that guarantees it.
 */
export const getExperienceSpots = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<{
  spots?: AgamiSpot[]
  error?: Record<string, unknown> | unknown
}> => {
  try {
    const { data, error } = await supabase
      .from(GEOXP_SPOTS_TABLE)
      .select('*, experience_content_spots!inner(manual_code, pattern_id)')
      .eq('is_canceled', false)
      .eq('is_draft', false)
      .eq('experience_content_spots.is_canceled', false)
      .eq('experience_content_spots.experience_id', experienceId)
      .order('updated_at', { ascending: false })

    if (error) {
      return { error }
    }

    if (data.length === 0) {
      return { spots: data }
    }

    const fileUrlPromises = data.map((spot: AgamiSpot) =>
      spot.audio_content_path
        ? getAudioFileUrl({
            bucketId: spot.content_media_type === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
            filePath: spot.audio_content_path,
          })
        : Promise.resolve(null)
    )

    const fileUrls = await Promise.all(fileUrlPromises)

    const spots = data.map((spot: AgamiSpot, idx: number) => ({
      ...spot,
      audioUrl: (fileUrls[idx]?.data as { signedUrl?: string })?.signedUrl,
    }))

    return { spots, error }
  } catch (error) {
    return { error }
  }
}

export const getSpotsByExperienceId = async ({
  userId,
  experienceId,
}: {
  userId: string
  experienceId: string
}): Promise<{
  spots?: AgamiSpot[]
  error?: Record<string, unknown> | unknown
}> => {
  try {
    // The satellite join is LEFT (no !inner): working drafts and published agami
    // have no submission and must stay in the list. RLS does the authorization by
    // itself — the satellite is readable only by the experience owner, so for
    // anyone else the embed is simply empty, with no condition in this code.
    // One query feeds three consumers (badge/counter 05.6, moderation 05.4,
    // form banner 05.5). The proposer's NAME is deliberately not embedded:
    // profiles is readable only by its own user, so a nested join would always
    // be null here — 05.4 resolves names through an edge function instead
    // (decided 2026-08-24).
    const { data, error } = await supabase
      .from(GEOXP_SPOTS_TABLE)
      .select(
        `*, experience_content_spots!inner(manual_code, pattern_id),
        experience_draft_submissions(id, submitted_by, submitted_at, original_title, outcome)`
      )
      .eq('user_id', userId)
      .eq('is_canceled', false)
      .eq('experience_content_spots.is_canceled', false)
      .eq('experience_content_spots.experience_id', experienceId)
      .order('updated_at', { ascending: false })

    if (error) {
      return { error }
    }

    if (data.length === 0) {
      return { spots: data }
    }

    const fileUrlPromises = data.map((spot: AgamiSpot) =>
      spot.audio_content_path
        ? getAudioFileUrl({
            bucketId: spot.content_media_type === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
            filePath: spot.audio_content_path,
          })
        : Promise.resolve(null)
    )

    const fileUrls = await Promise.all(fileUrlPromises)

    const spots = data.map((spot: AgamiSpot, idx: number) => ({
      ...spot,
      audioUrl: (fileUrls[idx]?.data as { signedUrl?: string })?.signedUrl,
    }))

    return { spots, error }
  } catch (error) {
    return { error }
  }
}
